import React, { useState, useEffect } from 'react';
import './ConversationLabels.css';

const PRESET_LABELS = [
  { value: 'friends', emoji: '👥', color: '#3b82f6' },
  { value: 'business', emoji: '💼', color: '#8b5cf6' },
  { value: 'archive', emoji: '📦', color: '#6b7280' },
  { value: 'spam', emoji: '🚫', color: '#ef4444' },
  { value: 'important', emoji: '⭐', color: '#f59e0b' },
  { value: 'follow-up', emoji: '📌', color: '#10b981' }
];

const ConversationLabels = ({ conversationId, onClose, onUpdate }) => {
  const [labels, setLabels] = useState([]);
  const [customPresets, setCustomPresets] = useState([]);
  const [customLabelName, setCustomLabelName] = useState('');
  const [customLabelColor, setCustomLabelColor] = useState('#667eea');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLabels();
    fetchCustomPresets();
  }, [conversationId]);

  const fetchLabels = async () => {
    try {
      const response = await fetch(
        `/api/messages/conversations/${conversationId}/labels`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        console.log('Fetched labels:', data); // Debug log
        setLabels(data.data || []);
      } else {
        console.error('Failed to fetch labels:', response.status);
      }
    } catch (err) {
      console.error('Error fetching labels:', err);
    }
  };

  const fetchCustomPresets = async () => {
    try {
      const response = await fetch('/api/messages/labels/presets', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCustomPresets(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching custom presets:', err);
    }
  };

  const addLabel = async (label, customName = null, customColor = null) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/messages/conversations/${conversationId}/labels`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            label,
            customLabelName: customName,
            color: customColor
          })
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Added label:', data); // Debug log
        // Refetch all labels to ensure consistency
        await fetchLabels();
        setCustomLabelName('');
        if (onUpdate) onUpdate();
      } else {
        const data = await response.json();
        console.error('Failed to add label:', data);
        alert(data.message || 'Failed to add label');
      }
    } catch (err) {
      console.error('Error adding label:', err);
      alert('Failed to add label');
    } finally {
      setLoading(false);
    }
  };

  const removeLabel = async (labelId) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/messages/conversations/${conversationId}/labels/${labelId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.ok) {
        // Refetch all labels to ensure consistency
        await fetchLabels();
        if (onUpdate) onUpdate();
      } else {
        console.error('Failed to remove label:', response.status);
        alert('Failed to remove label');
      }
    } catch (err) {
      console.error('Error removing label:', err);
      alert('Failed to remove label');
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomLabel = () => {
    if (customLabelName.trim().length === 0) {
      alert('Please enter a label name');
      return;
    }
    addLabel('custom', customLabelName.trim(), customLabelColor);
  };

  const deleteCustomPreset = async (presetId) => {
    try {
      const response = await fetch(`/api/messages/labels/presets/${presetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        await fetchCustomPresets();
      } else {
        alert('Failed to delete preset');
      }
    } catch (err) {
      console.error('Error deleting preset:', err);
      alert('Failed to delete preset');
    }
  };

  const hasLabel = (labelValue) => {
    return labels.some(l => l.label === labelValue);
  };

  const hasCustomLabel = (presetName) => {
    return labels.some(l => 
      l.label === 'custom' && l.customLabelName === presetName
    );
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="labels-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Conversation Labels</h2>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        <div className="labels-content">
          <div className="current-labels">
            <h3>Active Labels</h3>
            {labels.length > 0 ? (
              <div className="labels-list">
                {labels.map(label => (
                  <div
                    key={label._id}
                    className="label-chip"
                    style={{ background: `${label.color}20`, borderColor: label.color }}
                  >
                    <span className="label-text">
                      {label.label === 'custom' ? label.customLabelName : label.label}
                    </span>
                    <button
                      className="remove-label-btn"
                      onClick={() => removeLabel(label._id)}
                      disabled={loading}
                      style={{ color: label.color }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">No labels applied</div>
            )}
          </div>

          <div className="add-labels-section">
            <h3>Preset Labels</h3>
            <div className="preset-labels">
              {PRESET_LABELS.map(preset => (
                <button
                  key={preset.value}
                  className={`preset-label-btn ${hasLabel(preset.value) ? 'applied' : ''}`}
                  onClick={() => !hasLabel(preset.value) && addLabel(preset.value, null, preset.color)}
                  disabled={hasLabel(preset.value) || loading}
                  style={{ borderColor: preset.color }}
                >
                  <span className="label-emoji">{preset.emoji}</span>
                  <span className="label-name">{preset.value}</span>
                </button>
              ))}
            </div>
          </div>

          {customPresets.length > 0 && (
            <div className="custom-presets-section">
              <h3>Your Custom Presets</h3>
              <div className="custom-presets-list">
                {customPresets.map(preset => (
                  <div 
                    key={preset._id} 
                    className="custom-preset-item"
                    style={{ borderColor: preset.color }}
                  >
                    <button
                      className={`preset-use-btn ${hasCustomLabel(preset.name) ? 'applied' : ''}`}
                      onClick={() => !hasCustomLabel(preset.name) && addLabel('custom', preset.name, preset.color)}
                      disabled={hasCustomLabel(preset.name) || loading}
                      style={{ 
                        background: hasCustomLabel(preset.name) ? `${preset.color}40` : 'transparent',
                        borderColor: preset.color
                      }}
                    >
                      <span className="preset-color" style={{ background: preset.color }}></span>
                      <span className="preset-name">{preset.name}</span>
                    </button>
                    <button
                      className="delete-preset-btn"
                      onClick={() => deleteCustomPreset(preset._id)}
                      title="Delete this preset"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="custom-label-section">
            <h3>Create Custom Label</h3>
            <div className="custom-label-form">
              <div className="form-row">
                <input
                  type="text"
                  value={customLabelName}
                  onChange={e => setCustomLabelName(e.target.value)}
                  placeholder="Label name..."
                  maxLength={30}
                  className="custom-label-input"
                />
                <input
                  type="color"
                  value={customLabelColor}
                  onChange={e => setCustomLabelColor(e.target.value)}
                  className="color-picker"
                />
              </div>
              <button
                onClick={handleAddCustomLabel}
                className="add-custom-label-btn"
                disabled={loading || !customLabelName.trim()}
              >
                {loading ? 'Adding...' : 'Add Custom Label'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversationLabels;
